import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { lazy, useState } from "react";
import { RouteWorkspace } from "./app/RouteWorkspace";
import { useAuth } from "./lib/clerk";
const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));
const PublicLayout = lazy(() =>
  import("./layouts/PublicLayout").then((module) => ({
    default: module.PublicLayout,
  })),
);
import { Spinner } from "./components/ui/Spinner";
import {
  DocumentDetailRedirect,
  DocumentEditRedirect,
} from "./pages/documents/documentNavigation";
const AdminLayout = lazy(() =>
  import("./layouts/AdminLayout").then((module) => ({
    default: module.AdminLayout,
  })),
);
import { CookieNotice } from "./components/ui/CookieNotice";
import { Toaster } from "sonner";
import { useNetworkStatus } from "./hooks/useNetworkStatus";
import { SyncManager } from "./utils/syncManager";
import { useEffect } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ErrorPage } from "./components/ui/ErrorPage";
import { useNavigate } from "react-router-dom";
import { FeatureGuard } from "./hooks/useFeature";
import { AdminRouteGuard } from "./components/auth/AdminRouteGuard";

const NumberingWorkspace = lazy(() =>
  import("./features/settings/NumberingWorkspace").then((module) => ({
    default: module.NumberingWorkspace,
  })),
);
const InvoiceBalancesWorkspace = lazy(() =>
  import("./features/finance/InvoiceBalancesWorkspace").then((module) => ({
    default: module.InvoiceBalancesWorkspace,
  })),
);
const DocumentsList = lazy(() => import("./pages/documents/DocumentsList"));
const DocumentDetail = lazy(() => import("./pages/documents/DocumentDetail"));
const MagicEditor = lazy(() =>
  import("./pages/outbound/MagicEditor").then((module) => ({
    default: module.MagicEditor,
  })),
);
const ApprovalInbox = lazy(() =>
  import("./pages/approval/ApprovalInbox").then((module) => ({
    default: module.ApprovalInbox,
  })),
);
const ApprovalDetail = lazy(() =>
  import("./pages/approval/ApprovalDetail").then((module) => ({
    default: module.ApprovalDetail,
  })),
);
const Settings = lazy(() =>
  import("./pages/settings/Settings").then((module) => ({
    default: module.Settings,
  })),
);
const OrganizationSettings = lazy(() =>
  import("./pages/settings/organization/OrganizationSettings").then(
    (module) => ({ default: module.OrganizationSettings }),
  ),
);
const TeamSettings = lazy(() =>
  import("./pages/settings/team/TeamSettings").then((module) => ({
    default: module.TeamSettings,
  })),
);
const TaxSettings = lazy(() =>
  import("./pages/settings/TaxSettings").then((module) => ({
    default: module.TaxSettings,
  })),
);
const BillingSettings = lazy(() =>
  import("./pages/settings/BillingSettings").then((module) => ({
    default: module.BillingSettings,
  })),
);
const EmailSettings = lazy(() =>
  import("./pages/settings/integrations/EmailSettings").then((module) => ({
    default: module.EmailSettings,
  })),
);
const PeppolSettings = lazy(() =>
  import("./pages/settings/integrations/PeppolSettings").then((module) => ({
    default: module.PeppolSettings,
  })),
);
const TemplateSettings = lazy(() =>
  import("./pages/settings/templates/TemplateSettings").then((module) => ({
    default: module.TemplateSettings,
  })),
);
const TemplateEditor = lazy(() =>
  import("./pages/settings/templates/TemplateEditor").then((module) => ({
    default: module.TemplateEditor,
  })),
);
const DataExport = lazy(() =>
  import("./pages/settings/account/DataExport").then((module) => ({
    default: module.DataExport,
  })),
);
const DPA = lazy(() =>
  import("./pages/settings/legal/DPA").then((module) => ({
    default: module.DPA,
  })),
);
const WorkflowSettings = lazy(() =>
  import("./pages/settings/workflows/WorkflowSettings").then((module) => ({
    default: module.WorkflowSettings,
  })),
);
const CompliancePage = lazy(() =>
  import("./pages/public/CompliancePage").then((module) => ({
    default: module.CompliancePage,
  })),
);
const PricingPage = lazy(() =>
  import("./pages/marketing/PricingPage").then((module) => ({
    default: module.PricingPage,
  })),
);
const ReminderPreparationWorkspace = lazy(() =>
  import("./features/dunning/ReminderPreparationWorkspace").then((module) => ({
    default: module.ReminderPreparationWorkspace,
  })),
);
const DunningMonitor = lazy(() =>
  import("./pages/dunning/DunningMonitor").then((module) => ({
    default: module.DunningMonitor,
  })),
);
const DunningEditor = lazy(() =>
  import("./pages/dunning/DunningEditor").then((module) => ({
    default: module.DunningEditor,
  })),
);
const Archive = lazy(() =>
  import("./pages/archive/Archive").then((module) => ({
    default: module.Archive,
  })),
);
const Mailbox = lazy(() =>
  import("./pages/mailbox").then((module) => ({ default: module.Mailbox })),
);
const FinanceDashboard = lazy(() =>
  import("./pages/FinanceDashboard").then((module) => ({
    default: module.FinanceDashboard,
  })),
);
const PartnersList = lazy(() =>
  import("./pages/partners/PartnersList").then((module) => ({
    default: module.PartnersList,
  })),
);
const PartnerDetail = lazy(() =>
  import("./pages/partners/PartnerDetail").then((module) => ({
    default: module.PartnerDetail,
  })),
);
const AdminDashboard = lazy(() =>
  import("./pages/admin/Dashboard").then((module) => ({
    default: module.AdminDashboard,
  })),
);
const AdminFeatures = lazy(() =>
  import("./pages/admin/Features").then((module) => ({
    default: module.AdminFeatures,
  })),
);
const AdminPricing = lazy(() =>
  import("./pages/admin/Pricing").then((module) => ({
    default: module.AdminPricing,
  })),
);
const AdminTenants = lazy(() =>
  import("./pages/admin/Tenants").then((module) => ({
    default: module.AdminTenants,
  })),
);
const AdminTenantDetail = lazy(() =>
  import("./pages/admin/TenantDetail").then((module) => ({
    default: module.AdminTenantDetail,
  })),
);
const AdminTraces = lazy(() =>
  import("./pages/admin/Traces").then((module) => ({
    default: module.AdminTraces,
  })),
);
const AIOps = lazy(() =>
  import("./pages/admin/AIOps").then((module) => ({ default: module.AIOps })),
);
const OCR = lazy(() => import("./pages/admin/OCR"));
const PlatformSettings = lazy(() =>
  import("./pages/admin/PlatformSettings").then((module) => ({
    default: module.PlatformSettings,
  })),
);
const EmailProviders = lazy(() =>
  import("./pages/admin/EmailProviders").then((module) => ({
    default: module.EmailProviders,
  })),
);
const SupportQueue = lazy(() =>
  import("./pages/admin/SupportQueue").then((module) => ({
    default: module.SupportQueue,
  })),
);
const SupportTicketDetail = lazy(() =>
  import("./pages/admin/SupportTicketDetail").then((module) => ({
    default: module.SupportTicketDetail,
  })),
);
const UserTicketDetail = lazy(() =>
  import("./pages/support/UserTicketDetail").then((module) => ({
    default: module.UserTicketDetail,
  })),
);
const SupportDashboard = lazy(() =>
  import("./pages/support/SupportDashboard").then((module) => ({
    default: module.SupportDashboard,
  })),
);
const CreateTicketPage = lazy(() =>
  import("./pages/support/CreateTicketPage").then((module) => ({
    default: module.CreateTicketPage,
  })),
);
const Pipelines = lazy(() =>
  import("./pages/admin/Pipelines").then((module) => ({
    default: module.Pipelines,
  })),
);
const PipelineDesigner = lazy(() =>
  import("./pages/admin/PipelineDesigner").then((module) => ({
    default: module.PipelineDesigner,
  })),
);
const PipelineRunDetail = lazy(() =>
  import("./pages/admin/PipelineRunDetail").then((module) => ({
    default: module.PipelineRunDetail,
  })),
);
const Impressum = lazy(() =>
  import("./pages/legal/Impressum").then((module) => ({
    default: module.Impressum,
  })),
);
const PrivacyPolicy = lazy(() =>
  import("./pages/legal/PrivacyPolicy").then((module) => ({
    default: module.PrivacyPolicy,
  })),
);
const Terms = lazy(() =>
  import("./pages/legal/Terms").then((module) => ({ default: module.Terms })),
);
const Dashboard = lazy(() =>
  import("./pages/Dashboard").then((module) => ({ default: module.Dashboard })),
);
const AuthenticatedLandingRoute = lazy(() =>
  import("./components/auth/AuthenticatedLandingRoute").then((module) => ({
    default: module.AuthenticatedLandingRoute,
  })),
);

// Wrapper component for ErrorPage that uses useNavigate when inside Router context
function ErrorPageWithNavigate({ code }: { code?: number }) {
  const navigate = useNavigate();

  return (
    <ErrorPage code={code} onNavigateHome={() => navigate("/dashboard")} />
  );
}

const OfflineSyncHandler = () => {
  const isOnline = useNetworkStatus();
  const { isSignedIn } = useAuth();

  useEffect(() => {
    if (isOnline && isSignedIn) {
      SyncManager.replayMutations();
    }
  }, [isOnline, isSignedIn]);

  return null;
};

const ProtectedRoute = () => {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  return <Outlet />;
};

const AccountEntryPage = lazy(() =>
  import("./features/public/AccountEntryPage").then((module) => ({
    default: module.AccountEntryPage,
  })),
);

function App() {
  const [router] = useState(() =>
    createBrowserRouter(
      createRoutesFromElements(
        <Route
          element={
            <>
              <Toaster />
              <CookieNotice />
              <OfflineSyncHandler />
              <Outlet />
            </>
          }
        >
          <Route element={<RouteWorkspace />}>
            <Route
              path="/sign-in/*"
              element={<AccountEntryPage kind="sign-in" />}
            />
            <Route
              path="/sign-up/*"
              element={<AccountEntryPage kind="sign-up" />}
            />
          </Route>

          {/* Public Routes with Shared Layout */}
          <Route element={<RouteWorkspace />}>
            <Route element={<PublicLayout />}>
              <Route element={<RouteWorkspace />}>
                <Route path="/" element={<AuthenticatedLandingRoute />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/compliance" element={<CompliancePage />} />

                <Route path="/legal/impressum" element={<Impressum />} />
                <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                <Route path="/legal/terms" element={<Terms />} />
              </Route>
            </Route>
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route element={<RouteWorkspace />}>
              {/* Tenant Routes */}
              <Route element={<DashboardLayout />}>
                <Route element={<RouteWorkspace />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route
                    path="/invoices"
                    element={<Navigate to="/documents" replace />}
                  />
                  <Route path="/documents" element={<DocumentsList />} />
                  <Route path="/documents/new" element={<MagicEditor />} />
                  <Route path="/documents/:id" element={<DocumentDetail />} />
                  <Route path="/documents/:id/edit" element={<MagicEditor />} />
                  <Route
                    path="/invoices/:id"
                    element={<DocumentDetailRedirect />}
                  />
                  <Route
                    path="/outbound/new"
                    element={<Navigate to="/documents/new" replace />}
                  />
                  <Route
                    path="/outbound/:id"
                    element={<DocumentEditRedirect />}
                  />
                  <Route path="/finance" element={<FinanceDashboard />} />
                  <Route
                    path="/finance/balances"
                    element={<InvoiceBalancesWorkspace />}
                  />
                  <Route path="/approvals" element={<ApprovalInbox />} />
                  <Route path="/approvals/:id" element={<ApprovalDetail />} />
                  <Route path="/partners" element={<PartnersList />} />
                  <Route path="/partners/new" element={<PartnerDetail />} />
                  <Route path="/partners/:id" element={<PartnerDetail />} />
                  <Route
                    path="/dunning"
                    element={
                      <FeatureGuard
                        feature="feature_dunning_management"
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <DunningMonitor />
                      </FeatureGuard>
                    }
                  />
                  <Route
                    path="/dunning/prepare/:id"
                    element={
                      <FeatureGuard
                        feature="feature_dunning_management"
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <ReminderPreparationWorkspace />
                      </FeatureGuard>
                    }
                  />
                  <Route
                    path="/dunning/:id"
                    element={
                      <FeatureGuard
                        feature="feature_dunning_management"
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <DunningEditor />
                      </FeatureGuard>
                    }
                  />
                  <Route path="/archive" element={<Archive />} />
                  <Route path="/mailbox" element={<Mailbox />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route
                    path="/settings/organization"
                    element={<OrganizationSettings />}
                  />
                  <Route path="/settings/team" element={<TeamSettings />} />
                  <Route path="/settings/tax" element={<TaxSettings />} />
                  <Route
                    path="/settings/numbering"
                    element={<NumberingWorkspace />}
                  />
                  <Route path="/settings/email" element={<EmailSettings />} />
                  <Route
                    path="/settings/peppol"
                    element={
                      <FeatureGuard
                        feature="feature_peppol_send"
                        fallback={<Navigate to="/settings" replace />}
                      >
                        <PeppolSettings />
                      </FeatureGuard>
                    }
                  />
                  <Route
                    path="/settings/templates"
                    element={<TemplateSettings />}
                  />
                  <Route
                    path="/settings/templates/new"
                    element={<TemplateEditor />}
                  />
                  <Route
                    path="/settings/templates/:id"
                    element={<TemplateEditor />}
                  />
                  <Route
                    path="/settings/billing"
                    element={<BillingSettings />}
                  />
                  <Route path="/settings/export" element={<DataExport />} />
                  <Route path="/settings/dpa" element={<DPA />} />
                  <Route
                    path="/settings/workflows"
                    element={<WorkflowSettings />}
                  />
                  <Route path="/settings/pricing" element={<PricingPage />} />
                  <Route path="/support" element={<SupportDashboard />} />
                  <Route path="/support/new" element={<CreateTicketPage />} />
                  <Route path="/support/:id" element={<UserTicketDetail />} />
                </Route>
              </Route>

              {/* Admin Routes */}
              <Route
                path="/admin"
                element={
                  <AdminRouteGuard>
                    <AdminLayout />
                  </AdminRouteGuard>
                }
              >
                <Route element={<RouteWorkspace />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="tenants" element={<AdminTenants />} />
                  <Route path="tenants/:id" element={<AdminTenantDetail />} />
                  <Route path="features" element={<AdminFeatures />} />
                  <Route path="pricing" element={<AdminPricing />} />
                  <Route path="ai-ops" element={<AIOps />} />
                  <Route path="ocr" element={<OCR />} />
                  <Route path="email-providers" element={<EmailProviders />} />
                  <Route path="platform" element={<PlatformSettings />} />
                  <Route path="traces" element={<AdminTraces />} />
                  <Route path="support" element={<SupportQueue />} />
                  <Route path="support/:id" element={<SupportTicketDetail />} />
                  <Route path="pipelines" element={<Pipelines />} />
                  <Route path="pipelines/:id" element={<PipelineDesigner />} />
                  <Route
                    path="pipelines/runs/:id"
                    element={<PipelineRunDetail />}
                  />
                </Route>
              </Route>
            </Route>
          </Route>

          {/* Catch-all 404 - moved outside ProtectedRoute so it catches all unhandled paths */}
          <Route path="*" element={<ErrorPageWithNavigate code={404} />} />
        </Route>,
      ),
    ),
  );

  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}

export default App;
